// Date Functions
import { format, parseISO, formatDistance, formatDuration, formatISO, addDays } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { isNullOrUndefined, isNumeric } from './generalFunctions.js';
import * as libCon from '../Constants.js';



export const DATE_STRING = 'yyyy-MM-dd HH:mm:ss'
export const DATE_STRING_EXPORT = 'yyyy-MM-dd_HH:mm:ss'
export const DATE_STRING_SHORT = 'yyyy-MM-dd'

// The study's IANA timezone, from config/general*.js. Read at call time rather
// than at import, so this module does not depend on Constants.js finishing first.
const studyTimezone = () => libCon.GENERAL_CONFIG.timezone

// A date, or a date and time, with no Z and no offset.
const NAIVE_DATE_TIME = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?)?$/

/**
 * Turns a value into the instant it stands for.
 *
 * Every datetime this project stores is naive and in the study's timezone (see
 * config/README.md), so a string without a Z or an offset is read as study time.
 * parseISO would read it as the host's time instead, which only agrees when the
 * phone or the server happens to sit in the study's timezone.
 *
 * @param {*} value a Date, epoch milliseconds, or an ISO string with or without an offset
 * @returns a Date
 */
export const parseInStudyLocation = (value) => {
  if (value instanceof Date)
    return value
  if (isNumeric(value))
    return new Date(value)
  if (typeof value === 'string' && NAIVE_DATE_TIME.test(value.trim()))
    return fromZonedTime(value.trim(), studyTimezone())

  return parseISO(value)
}


// formatExport, formatISOCustom and formatISOCustomShort format in the HOST's
// timezone and convert nothing. They are only right for re-formatting a value
// that is already local, such as Fitbit's naive lastSyncTime. For "now", or for
// an instant coming back from Airtable or Google, use the *StudyLocation*
// functions below.
export const formatExport = (date) => {

  date = new Date(date)

  return (format(date, DATE_STRING_EXPORT))

}

export const formatISOCustom = (date) => {
  date = new Date(date)

  return (format(date, DATE_STRING))

}

export const formatISOCustomShort = (date) => {
  date = new Date(date)

  return (format(date, DATE_STRING_SHORT))

}

export function formatMinutes(minutes) {

  minutes = Math.floor(minutes)
  let hours = Math.floor(minutes / 60);
  let mins = minutes % 60;
  let paddedMins = mins.toString().padStart(2, '0');
  return `${hours}:${paddedMins}`;
}

export const getDaysAgo = (d1) => {
  return (getDaysBetweenDates(d1, nowInStudyLocation()))
}


export const getDaysBetweenDates = (d1, d2) => {

  // Convert both dates to milliseconds
  let date1Ms = new Date(d1).getTime();
  let date2Ms = new Date(d2).getTime();

  // Calculate the difference in milliseconds
  let differenceMs = date2Ms - date1Ms;

  // Convert back to days
  let differenceDays = differenceMs / (1000 * 60 * 60 * 24);

  return differenceDays;

}

export const getMinutesBetweenDates = (d1, d2) => {

  // Convert both dates to milliseconds
  let date1Ms = new Date(d1).getTime();
  let date2Ms = new Date(d2).getTime();

  // Calculate the difference in milliseconds
  let differenceMs = date2Ms - date1Ms;

  // Convert back to days
  let differenceDays = differenceMs / (1000 * 60);

  return differenceDays;

}


export const nowInStudyLocation = (includeTime = false) => {
  return (formatToStudyLocation(new Date(), includeTime))
}

// RFC 3339 without the milliseconds, matching the RFC_3339_FORMAT the Python
// library sends. The two build the same request, so they format it the same way.
const toRFC3339 = (date) => date.toISOString().replace(/\.\d{3}Z$/, 'Z')

/**
 * The [start, end) UTC instants bounding a closed range of study calendar days,
 * as RFC 3339.
 *
 * The Google Health API works in physical (UTC) time while everything this
 * project stores is local, so "the twelfth of May in Ahmedabad" has to become
 * [2026-05-11T18:30:00Z, 2026-05-12T18:30:00Z) before it can be asked for.
 *
 * The day is taken as a 'yyyy-MM-dd' string verbatim rather than round tripped
 * through a Date: new Date('2026-05-12') is UTC midnight, and re-formatting that
 * on any machine west of UTC yields the eleventh.
 *
 * The exclusive end is midnight of the following calendar day, not the start
 * plus 24 hours, so a timezone with daylight saving gets its 23 and 25 hour
 * days right.
 *
 * The port of local_range_bounds_utc in the Python library.
 *
 * @param {*} firstDay the first study day in the range, inclusive
 * @param {*} lastDay the last study day in the range, also inclusive
 * @returns [startUTC, endUTC] as RFC 3339 strings
 */
export const localRangeBoundsUTC = (firstDay, lastDay) => {

  const asDayString = (value) =>
    (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) ? value : formatToStudyLocation(value)

  // Calendar arithmetic on the day itself, done in UTC so the host cannot shift it
  const nextDayString = (day) => addDays(new Date(`${day}T00:00:00Z`), 1).toISOString().slice(0, 10)

  const start = fromZonedTime(`${asDayString(firstDay)}T00:00:00`, studyTimezone())
  // The range is closed on both ends, so the exclusive bound is the day after
  const end = fromZonedTime(`${nextDayString(asDayString(lastDay))}T00:00:00`, studyTimezone())

  return [toRFC3339(start), toRFC3339(end)]
}

/**
 * The [start, end) UTC instants bounding one study calendar day, as RFC 3339.
 *
 * The port of local_day_bounds_utc in the Python library.
 *
 * @param {*} day
 * @returns [startUTC, endUTC] as RFC 3339 strings
 */
export const localDayBoundsUTC = (day) => localRangeBoundsUTC(day, day)


export const timeNowInStudyLocation = (includeSeconds = false) => {

  // Format the date in the desired output format
  let format = "HH:mm"
  if (includeSeconds)
    format = "HH:mm:ss"

  return formatInTimeZone(new Date(), studyTimezone(), format)

}

export const formatInStudyLocationToCustom = (str, format) => {

  if (isNullOrUndefined(str) || str === Infinity || str === -Infinity)
    return str

  return formatInTimeZone(parseInStudyLocation(str), studyTimezone(), format)
}

export const formatToStudyLocation = (str, includeTime = false) => {

  // Format the date in the desired output format
  let format = DATE_STRING_SHORT
  if (includeTime)
    format = DATE_STRING

  return formatInStudyLocationToCustom(str, format)
}



export const formatToStudyLocationReadable = (str, includeTime = false, includeYear = true, includeSeconds = false) => {

  if (isNullOrUndefined(str))
    return str

  // Format the date in the desired output format
  let format = "MMMM dd"
  if (includeYear)
    format += " yyyy"
  if (includeTime)
    format += " HH:mm"
  if (includeSeconds)
    format += ":ss"

  return formatInTimeZone(parseInStudyLocation(str), studyTimezone(), format)
}






export const formatDistanceShort = (str) => {

  const date = new Date(str);

  if (isNaN(date.getTime()))
    return ("Never")


  let response = formatDistance(date, new Date(), { addSuffix: true })

  response = response.replace("about ", "")

  return response
}

export const formatDistanceGivenDays = (days, includeWording = true) => {
  let sign = Math.sign(days)

  days = Math.abs(days)

  let decimalPart = days - Math.floor(days)
  let hours = Math.floor(decimalPart * 24)
  let minutes = Math.floor(decimalPart * (24 * 60))
  let seconds = Math.floor(decimalPart * (24 * 60 * 60))

  let response = ""
  if (days >= 1)
    response = formatDuration({ days: Math.floor(days) })
  else if (hours >= 1)
    response = formatDuration({ hours: hours })
  else if (minutes >= 1)
    response = formatDuration({ hours: minutes })
  else
    response = formatDuration({ seconds: Math.max(1, seconds) })

  if (!includeWording)
    return response

  if (sign >= 0)
    return `In ${response}`
  else
    return `${response} ago`


}

export const nowISO = () => {
  return (formatISO(new Date()))
}

